import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { getUserProfile } from "../api/auth";
import { Alert, Button } from "../ds";
import NoRoleAssigned from "../views/NoRoleAssigned";
import ForceLightTheme from "../theme/ForceLightTheme";

export default function RequireAuth({ children }: { children: ReactNode }) {
  const { auth } = useAuth();
  const location = useLocation();

  // Per-pathname key + placeholderData re-fetches on every nav while
  // backing the UI with the previous result so we don't flash a spinner
  // mid-app. A first mount with no cached data still shows the spinner.
  const userId = auth?.user.id ?? "";
  const profileQuery = useQuery({
    queryKey: ["ess-profile-gate", userId, location.pathname],
    queryFn: () => getUserProfile(userId).then((r) => r.user),
    enabled: Boolean(auth),
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    placeholderData: (prev) => prev,
  });

  if (!auth) {
    return (
      <Navigate to="/login" replace state={{ from: location.pathname }} />
    );
  }

  if (profileQuery.isError) {
    return (
      <Alert severity="error" action={<Button onClick={() => void profileQuery.refetch()}>Retry</Button>}>
        Unable to load your profile. Please try again.
      </Alert>
    );
  }

  if (!profileQuery.data) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: "100dvh",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const profile = profileQuery.data;
  const hasRole =
    Boolean(profile.role && profile.role.trim()) &&
    (profile.roles?.length ?? 0) > 0;

  if (!hasRole) {
    // Same unauthenticated paper palette as Login — pinned light.
    return (
      <ForceLightTheme>
        <NoRoleAssigned />
      </ForceLightTheme>
    );
  }

  // Admin-controlled gate: a user who has ESS roles but hasn't been
  // verified yet is held on /profile (which renders a "pending" banner)
  // and can change their password, but cannot enter the rest of the
  // app. Self-service profile edits stay reachable so they're not
  // locked out of fixing their own info while they wait.
  const isEssVerified = profile.user_info?.ess_verified === true;
  const isOnPendingAllowedPath =
    location.pathname === "/profile" ||
    location.pathname === "/change-password";

  if (!isEssVerified && !isOnPendingAllowedPath) {
    return <Navigate to="/profile" replace />;
  }

  return <>{children}</>;
}
